import { AppSelect } from '../../ui/AppSelect';
import React, { useMemo, useState } from 'react';
import { X, Play, ArrowRight, Film, Clock, Zap } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import {
  getSequenceShotImage,
  getSequenceVideoDurationOptions,
  normalizeSequenceVideoDuration,
  resolveSequenceVideoRuntime,
} from '../../../utils/sequenceVideoCapabilities';
import {
  normalizeWanxVideoModel,
  WANX_VIDEO_MODEL,
  WANX_VIDEO_MODELS,
  WANX_VIDEO_RESOLUTIONS,
} from '../../../utils/wanxVideoCapabilities';

const SequenceVideoPanel = ({ shots, projectId, onGenerateSequence, onClose, isGenerating, exiting }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [provider, setProvider] = useState('vidu');
  const [model, setModel] = useState('viduq3-pro');
  const [resolution, setResolution] = useState('1080P');
  const runtime = useMemo(() => resolveSequenceVideoRuntime({
    projectId,
    provider,
    model,
    resolution,
  }), [projectId, provider, model, resolution]);
  const durationOptions = useMemo(
    () => getSequenceVideoDurationOptions(runtime.provider),
    [runtime.provider],
  );

  // 为每个分镜存储单独的时长设置
  const [shotDurations, setShotDurations] = useState(() => {
    // 初始化：使用分镜的建议时长（time 字段），默认 5s
    const durations = {};
    shots.forEach(shot => {
      const suggestedTime = shot.time || '5s';
      const timeValue = parseInt(suggestedTime.replace('s', ''), 10) || 5;
      durations[shot.id] = normalizeSequenceVideoDuration(runtime.provider, timeValue, 5);
    });
    return durations;
  });

  const handleGenerate = () => {
    if (runtime.unavailable) return;
    const normalizedDurations = Object.fromEntries(
      Object.entries(shotDurations).map(([shotId, duration]) => [
        shotId,
        normalizeSequenceVideoDuration(runtime.provider, duration, 5),
      ]),
    );
    onGenerateSequence({
      provider: runtime.provider,
      model: runtime.model,
      resolution: runtime.resolution,
      shotDurations: normalizedDurations,
    });
  };

  const handleDurationChange = (shotId, duration) => {
    setShotDurations(prev => ({
      ...prev,
      [shotId]: normalizeSequenceVideoDuration(runtime.provider, duration, 5)
    }));
  };

  const handleProviderChange = (nextProvider) => {
    setProvider(nextProvider);
    if (nextProvider === 'wanx') {
      setModel(WANX_VIDEO_MODEL);
      setResolution('1080P');
    } else if (nextProvider === 'vidu') {
      setModel('viduq3-pro');
    } else if (nextProvider === 'minimax') {
      setModel('MiniMax-Hailuo-2.3');
    }
    setShotDurations((previous) => Object.fromEntries(
      Object.entries(previous).map(([shotId, duration]) => [
        shotId,
        normalizeSequenceVideoDuration(nextProvider, duration, 5),
      ]),
    ));
  };

  // 过滤出有图片的分镜（首尾帧模式使用 generated_image）
  const validShots = shots.filter(shot => getSequenceShotImage(shot));
  const hasValidShots = validShots.length >= 2;
  const sequenceCount = Math.max(0, validShots.length - 1);

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--overlay-medium)] backdrop-blur-sm ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}>
      <div
        className={`w-full max-w-6xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: theme.colors.accentPrimary }}
            >
              <Film size={20} style={{ color: "var(--color-accent-contrast)" }} />
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
                视频序列生成
              </h3>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                首尾帧连续视频模式
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {!hasValidShots ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-bg-overlay)' }}
              >
                <Film size={32} style={{ color: 'var(--color-text-muted)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                没有可用的图片，请先生成分镜图片
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {validShots.map((shot, index) => {
                // 首尾帧模式：使用 generated_image
                // 首帧 = 当前分镜的图片
                // 尾帧 = 下一个分镜的图片（如果是最后一个分镜，则使用当前分镜的图片）
                const firstFrame = getSequenceShotImage(shot);
                const nextShot = validShots[index + 1];
                const lastFrame = nextShot
                  ? getSequenceShotImage(nextShot)
                  : (shot.lastFrameImage || shot.last_frame_image || firstFrame);

                return (
                  <div key={shot.id} className="space-y-3">
                    {/* Shot Info */}
                    <div className="flex items-center gap-3">
                      <div
                        className="px-3 py-1 rounded-lg text-xs font-bold"
                        style={{
                          backgroundColor: theme.colors.accentPrimary,
                          color: 'white'
                        }}
                      >
                        分镜 {shot.shotNumber || shot.shot_number || index + 1}
                      </div>
                      <p
                        className="text-sm flex-1 line-clamp-1"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {shot.visualContent || shot.visual_content || '无描述'}
                      </p>
                    </div>

                    {/* Frame Pair */}
                    <div
                      className="flex items-center gap-4 p-4 rounded-xl"
                      style={{
                        backgroundColor: 'var(--color-bg-overlay)',
                        border: '1px solid var(--color-border-primary)'
                      }}
                    >
                      {/* First Frame */}
                      <div className="flex-1">
                        <div className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>
                          首帧
                        </div>
                        <div className="aspect-video rounded-lg overflow-hidden" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                          {firstFrame ? (
                            <img
                              src={firstFrame}
                              alt="首帧"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                无图片
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Arrow + Duration Selector */}
                      <div className="flex flex-col items-center gap-2">
                        <ArrowRight size={24} style={{ color: theme.colors.accentPrimary }} />
                        <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                          视频
                        </span>

                        {/* Duration Selector */}
                        <div className="flex flex-col items-center gap-1 mt-2">
                          <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                            建议: {shot.time || '5s'}
                          </span>
                          <AppSelect
                            value={shotDurations[shot.id] || 5}
                            onChange={(e) => handleDurationChange(shot.id, e.target.value)}
                            className="px-2 py-1 rounded text-xs outline-none"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              border: '1px solid var(--color-border-primary)',
                              color: 'var(--color-text-primary)'
                            }}
                            disabled={isGenerating}
                          >
                            {durationOptions.map((duration) => (
                              <option key={duration} value={duration}>
                                {duration}s
                              </option>
                            ))}
                          </AppSelect>
                        </div>
                      </div>

                      {/* End Frame */}
                      <div className="flex-1">
                        <div className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>
                          {index < validShots.length - 1 ? '尾帧（下一镜）' : '尾帧'}
                        </div>
                        <div className="aspect-video rounded-lg overflow-hidden" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                          {lastFrame ? (
                            <img
                              src={lastFrame}
                              alt="尾帧"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                无图片
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Continuity Indicator */}
                    {index < validShots.length - 1 && (
                      <div className="flex items-center justify-center py-2">
                        <div
                          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: 'var(--color-accent-light)',
                            color: theme.colors.accentPrimary
                          }}
                        >
                          <Zap size={14} />
                          <span>连接到下一镜</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Controls */}
        <div
          className="px-6 py-4 border-t"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-primary)'
          }}
        >
          <div className="flex items-center gap-4">
            {/* Provider Selection */}
            <div className="flex-1">
              <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                视频提供商
              </label>
              <AppSelect
                value={runtime.fixed ? runtime.provider : provider}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-overlay)',
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)'
                }}
                disabled={isGenerating || runtime.fixed}
              >
                {runtime.unavailable ? (
                  <option value="">项目视频配置未加载</option>
                ) : runtime.fixed ? (
                  <option value={runtime.provider}>{runtime.model || runtime.provider}</option>
                ) : (
                  <>
                    <option value="vidu">Vidu</option>
                    <option value="minimax">MiniMax</option>
                    <option value="wanx">万相 3.0</option>
                  </>
                )}
              </AppSelect>
            </div>

            {/* Model Selection */}
            {runtime.fixed && (
              <div className="flex-1">
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                  模型
                </label>
                <div
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-bg-overlay)',
                    border: '1px solid var(--color-border-primary)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  Seedance 2.0 · 720p
                </div>
              </div>
            )}
            {!runtime.fixed && provider === 'vidu' && (
              <div className="flex-1">
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                  模型
                </label>
                <AppSelect
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--color-bg-overlay)',
                    border: '1px solid var(--color-border-primary)',
                    color: 'var(--color-text-primary)'
                  }}
                  disabled={isGenerating}
                >
                  <option value="viduq3-turbo">Q3 Turbo</option>
                  <option value="viduq3-pro">Q3 Pro</option>
                  <option value="viduq2-pro-fast">Q2 Pro Fast</option>
                  <option value="viduq2-pro">Q2 Pro</option>
                  <option value="viduq2-turbo">Q2 Turbo</option>
                  <option value="viduq1">Q1</option>
                  <option value="viduq1-classic">Q1 Classic</option>
                  <option value="vidu2.0">Vidu 2.0</option>
                </AppSelect>
              </div>
            )}
            {!runtime.fixed && provider === 'wanx' && (
              <>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                    模型
                  </label>
                  <AppSelect
                    value={normalizeWanxVideoModel(model)}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--color-bg-overlay)',
                      border: '1px solid var(--color-border-primary)',
                      color: 'var(--color-text-primary)'
                    }}
                    disabled={isGenerating}
                  >
                    {WANX_VIDEO_MODELS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </AppSelect>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--color-text-muted)' }}>
                    分辨率
                  </label>
                  <AppSelect
                    value={runtime.resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--color-bg-overlay)',
                      border: '1px solid var(--color-border-primary)',
                      color: 'var(--color-text-primary)'
                    }}
                    disabled={isGenerating}
                  >
                    {WANX_VIDEO_RESOLUTIONS.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </AppSelect>
                </div>
              </>
            )}

            {/* Generate Button */}
            <div className="flex-1">
              <label className="text-xs font-medium mb-2 block opacity-0">
                操作
              </label>
              <button
                onClick={handleGenerate}
                disabled={!hasValidShots || isGenerating || runtime.unavailable}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: theme.colors.accentPrimary,
                  color: 'white'
                }}
              >
                {isGenerating ? (
                  <>
                    <Clock size={16} className="animate-spin" />
                    <span>生成中...</span>
                  </>
                ) : (
                  <>
                    <Play size={16} fill="currentColor" />
                    <span>生成全部视频</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 flex items-center justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
            <span>共 {sequenceCount} 个连续片段 · 总时长：<span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>{validShots.slice(0, -1).reduce((sum, shot) => sum + (shotDurations[shot.id] || 0), 0)}s</span></span>
            <span>预计耗时：{sequenceCount * 90} - {sequenceCount * 120} 秒</span>
          </div>
          {runtime.provider === 'wanx' && (
            <p className="mt-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              相邻图片将作为首尾帧提交；任务成功后系统会立即转存视频，服务商结果链接仅保留 24 小时。
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SequenceVideoPanel;
